import { defineStore } from "pinia";
import { store } from "/@/store";
import { router } from "/@/router";
import { h } from "vue";
import {
  AccountType,
  LoginParams,
  ResetPasswordParams,
  SignupParams,
  UserInfo,
} from "@/models/user";
import { PageEnum } from "@/enums/pageEnum";
import { usePermissionStore } from "/@/store/modules/permission";
import { InputPassword, message, Modal } from "ant-design-vue";
import axios from "@/http/axios";
import { useRegisterStore } from "./register";

const LOGIN_TOKEN = () =>
  `LOGIN_TOKEN_${import.meta.env.VITE_PROJECT_ALIAS.toUpperCase()}`;

export const useUserStore = defineStore({
  id: "app-user",
  state: () => ({
    userInfo: null as Nullable<UserInfo>,
    token: undefined as string | undefined,
  }),
  getters: {
    getUserInfo(): UserInfo | null {
      return this.userInfo;
    },
    getToken(): string {
      return this.token || localStorage.getItem(LOGIN_TOKEN()) || "";
    },
    getUserDesc(): string {
      return this.userInfo?.account || this.userInfo?.phone || "";
    },
    getHomePage(): string {
      return PageEnum.HOME_PAGE;
    },
    isRoot(): boolean {
      return this.userInfo?.type == AccountType.Root;
    },
    isProjectAdmin(): boolean {
      return this.isRoot || (this.userInfo?.adminProjects?.length ?? 0) > 0;
    },
    isOrganizationAdmin(): boolean {
      return (
        this.isProjectAdmin ||
        (this.userInfo?.adminOrganizations?.length ?? 0) > 0
      );
    },
    isOrganizationReviewer(): boolean {
      return (
        this.isOrganizationAdmin ||
        (this.userInfo?.reviewerOrganizations?.length ?? 0) > 0
      );
    },
  },
  actions: {
    setToken(token: string | null) {
      this.token = token ?? "";
      localStorage.setItem(LOGIN_TOKEN(), token ?? "");
    },
    setUserInfo(info: UserInfo | null) {
      this.userInfo = info ?? null;
    },
    resetState() {
      this.setToken(null);
      this.setUserInfo(null);
    },
    async login(
      params: LoginParams & {
        redirect?: string;
      },
    ): Promise<UserInfo | null> {
      const data = await axios.get<{ token: string }>({
        url: "auth/:account",
        params: { account: params.account },
        query: {
          password: params["password"] || "empty",
          captcha: params["captcha"],
        },
      });
      if (!data) return null;

      this.setToken(data.token);
      const { redirect } = params;
      const userInfo = await this.afterLoginAction();
      if (userInfo) {
        await this.goHome(redirect);
      }
      return userInfo;
    },
    async signup(
      params: SignupParams & {
        redirect?: string;
      },
    ): Promise<UserInfo | null> {
      const data = await axios.post<{ token: string }>({
        url: "auth",
        data: {
          phone: params.phone,
          password: params.password,
          captcha: params.captcha,
        },
      });
      if (!data) return null;

      this.setToken(data.token);
      const { redirect } = params;
      const userInfo = await this.afterLoginAction();
      if (userInfo) {
        await this.goHome(redirect);
      }
      return userInfo;
    },
    async resetPassword(
      params: ResetPasswordParams & {
        redirect?: string;
      },
    ): Promise<UserInfo | null> {
      const data = await axios.patch<{ token: string }>({
        url: "auth/password",
        data: {
          phone: params.phone,
          password: params.password,
          captcha: params.captcha,
        },
      });
      if (!data) return null;

      this.setToken(data.token);
      const { redirect } = params;
      const userInfo = await this.afterLoginAction();
      if (userInfo) {
        await this.goHome(redirect);
      }
      return userInfo;
    },
    async goHome(redirect?: string): Promise<void> {
      if (redirect == PageEnum.BASE_LOGIN) {
        redirect = undefined;
      }
      if (!redirect) {
        redirect =
          (router.currentRoute.value.query.redirect as string) || undefined;
      }

      await router.replace(redirect ?? this.getHomePage);
    },
    async afterLoginAction(goHome?: boolean): Promise<UserInfo | null> {
      if (!this.getToken) return null;
      const userInfo = await this.getUserInfoAction();
      if (!userInfo) return null;

      if (userInfo.type == AccountType.Normal) {
        const registerStore = useRegisterStore();
        await registerStore.loadRegisters();
      }

      const permissionStore = usePermissionStore();
      await permissionStore.rebuildRoutesAction();
      goHome && (await router.replace(this.getHomePage));

      return userInfo;
    },
    async getUserInfoAction(): Promise<UserInfo | null> {
      if (!this.getToken) return null;
      const userInfo = await axios.get<UserInfo>({
        url: "user",
      });
      if (!userInfo) return null;

      if (!userInfo.isPasswordInited) {
        await new Promise<void>((resolve) => {
          let password = "";
          Modal.info({
            title: "请设置初始密码",
            content: h(InputPassword, {
              placeholder: "输入密码",
              "onUpdate:value": (val) => (password = val),
              style: "margin-top:6px",
            }),
            icon: null,
            closable: false,
            okText: "确认",
            async onOk() {
              if (!password) {
                message.warning("请输入密码");
                throw "";
              }
              if (password.length < 8) {
                message.warning("密码长度不能小于 8");
                throw "";
              }
              const res = await axios.post({
                url: "user/password/init",
                data: {
                  password: password,
                },
              });
              if (!res) throw "";

              resolve();
            },
          });
        });
      }

      this.setUserInfo(userInfo);
      return userInfo;
    },
    async logout(goLogin = false, useApi = false): Promise<boolean> {
      if (useApi && !!this.getToken) {
        await axios.delete({
          url: "auth",
        });
      }
      this.resetState();

      const permissionStore = usePermissionStore();
      permissionStore.resetState();

      const registerStore = useRegisterStore();
      registerStore.resetState();

      if (goLogin) {
        await router.push({
          path: "/login",
        });
      }
      return true;
    },
    confirmLoginOut() {
      Modal.confirm({
        iconType: "warning",
        title: () => h("span", "温馨提醒"),
        content: () => h("span", "是否确认退出系统?"),
        onOk: async () => {
          await this.logout(true);
        },
        onCancel: async () => {},
        okText: "确认退出",
        cancelText: "取消",
      });
    },
  },
});

// Need to be used outside the setup
export function useUserStoreWithOut() {
  return useUserStore(store);
}
